import { NavLink, useLocation } from 'react-router-dom';
import { ClipboardList, MessagesSquare, Network, type LucideIcon } from 'lucide-react';
import { useAuth } from '@/features/auth';
import type { Permissions } from '@/shared/api';
import { Logo } from '@/shared/ui/logo';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from '@/shared/ui/shadcn/sidebar';
import { Avatar, AvatarFallback } from '@/shared/ui/shadcn/avatar';

type NavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  permission?: keyof Permissions;
};

type NavGroup = {
  label: string;
  items: NavItem[];
};

const navGroups: NavGroup[] = [
  {
    label: 'Improving',
    items: [
      { to: '/playground', label: 'Playground', icon: MessagesSquare },
      {
        to: '/knowledge',
        label: 'Knowledge',
        icon: Network,
        permission: 'can_edit_knowledge_base',
      },
      { to: '/remarks', label: 'Remarks', icon: ClipboardList },
    ],
  },
];

export function AppSidebar() {
  const { user, hasPermission } = useAuth();
  const location = useLocation();
  const initials =
    user?.name
      ?.split(/\s+/)
      .map((p) => p[0])
      .join('')
      .slice(0, 2)
      .toUpperCase() || 'A';

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              className="group-data-[collapsible=icon]:p-2!"
              render={<NavLink to="/playground" />}
              tooltip="ARIA"
            >
              <Logo
                variant="mark"
                className="size-8 shrink-0 group-data-[collapsible=icon]:size-4"
              />
              <div className="grid min-w-0 flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                <span className="truncate font-semibold">ARIA</span>
                <span className="truncate text-xs text-muted-foreground">Admin</span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        {navGroups.map((group) => {
          const items = group.items.filter(
            (item) => !item.permission || hasPermission(item.permission),
          );
          if (items.length === 0) return null;
          return (
            <SidebarGroup key={group.label}>
              <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {items.map((item) => (
                    <SidebarMenuItem key={item.to}>
                      <SidebarMenuButton
                        render={<NavLink to={item.to} />}
                        tooltip={item.label}
                        isActive={
                          location.pathname === item.to ||
                          location.pathname.startsWith(`${item.to}/`)
                        }
                      >
                        <item.icon />
                        <span>{item.label}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          );
        })}
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              render={<NavLink to="/profile" />}
              tooltip={user?.name ?? 'Profile'}
              isActive={location.pathname === '/profile'}
            >
              <Avatar className="size-8 shrink-0 rounded-lg">
                <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
              </Avatar>
              <div className="grid min-w-0 flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                <span className="truncate font-semibold">{user?.name}</span>
                <span className="truncate text-xs text-muted-foreground">{user?.role_key}</span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
